import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '@/api/client'
import { ArrowLeft, Search, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'

function countryFlag(code: unknown): string {
  if (!code || typeof code !== 'string' || code.length !== 2) return ''
  const upper = code.toUpperCase()
  const codePoints = [...upper].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  return String.fromCodePoint(...codePoints)
}

interface ColumnInfo {
  name: string
  data_type: string
  max_length?: number
  is_nullable: string
  default?: string
}

interface SchemaInfo {
  table: string
  pk_column: string
  columns: ColumnInfo[]
}

const TABLE_LABELS: Record<string, string> = {
  countries: 'Countries',
  currencies: 'Currencies',
  languages: 'Languages',
  document_types: 'Document Types',
  country_document_types: 'Country Document Types',
  gaming_jurisdictions: 'Gaming Jurisdictions',
  jurisdiction_countries: 'Jurisdiction Countries',
  timezones: 'Timezones',
  banks: 'Banks',
  payment_methods: 'Payment Methods',
  bank_payment_methods: 'Bank Payment Methods',
  country_payment_methods: 'Country Payment Methods',
  bank_account_formats: 'Bank Account Formats',
  country_bank_account_formats: 'Country Account Formats',
  products: 'Products',
  permissions: 'Permissions',
  roles: 'Roles',
  role_permissions: 'Role Permissions',
}

const PAGE_SIZE = 25

export function CatalogTablePage() {
  const { table } = useParams<{ table: string }>()
  const navigate = useNavigate()

  const [schema, setSchema] = useState<SchemaInfo | null>(null)
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchSchema = () => {
    if (!table) return
    api.get<SchemaInfo>(`/catalog/${table}/schema`).then(({ data }) => setSchema(data))
  }

  const fetchRows = () => {
    if (!table) return
    setLoading(true)
    const params: Record<string, string | number> = { limit: PAGE_SIZE, offset: page * PAGE_SIZE }
    if (search) params.search = search
    api.get(`/catalog/${table}`, { params })
      .then(({ data }) => {
        setRows(data.data || [])
        setTotal(data.total || 0)
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchSchema() }, [table])
  useEffect(() => { fetchRows() }, [table, page, search])

  // Tables that have _countries enrichment from the API
  const tablesWithCountries = ['document_types', 'gaming_jurisdictions', 'languages', 'payment_methods']
  const hasCountryColumn = table ? tablesWithCountries.includes(table) : false

  const hiddenColumns = ['website', 'preview_url']

  const visibleColumns = useMemo(() => {
    if (!schema) return []
    return schema.columns.filter(col => !hiddenColumns.includes(col.name))
  }, [schema])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const renderCellValue = (value: unknown, col: ColumnInfo, row: Record<string, unknown>) => {
    if (value === null || value === undefined) return <span className="text-muted-foreground italic">null</span>
    if (col.data_type === 'bit') return value ? 'Yes' : 'No'
    // Show flag for country name column
    if (col.name === 'name' && (table === 'countries') && row['code']) {
      return <>{countryFlag(row['code'])} {String(value)}</>
    }
    // Show flag for country_code FK columns
    if (col.name === 'country_code') {
      return <>{countryFlag(value)} {String(value)}</>
    }
    return String(value)
  }

  const handleRowClick = (row: Record<string, unknown>) => {
    if (!schema) return
    const pk = row[schema.pk_column]
    if (pk !== null && pk !== undefined) {
      navigate(`/catalog/${table}/${pk}`)
    }
  }

  if (!table) return null

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-card rounded-lg transition-colors">
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{TABLE_LABELS[table] || table}</h1>
            <p className="text-sm text-muted-foreground">catalog.{table} &middot; {total} rows</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by name or code..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0) }}
          className="w-full bg-card border border-border rounded-lg pl-10 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent"
        />
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {visibleColumns.map((col) => (
                  <th key={col.name} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                    {col.name}
                    <span className="ml-1 text-[10px] font-normal normal-case text-muted-foreground/60">({col.data_type})</span>
                  </th>
                ))}
                {hasCountryColumn && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                    Countries
                  </th>
                )}
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleColumns.length + (hasCountryColumn ? 2 : 1)} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={visibleColumns.length + (hasCountryColumn ? 2 : 1)} className="px-4 py-8 text-center text-muted-foreground">No data found</td></tr>
              ) : (
                rows.map((row, i) => (
                  <tr
                    key={i}
                    onClick={() => handleRowClick(row)}
                    className="border-b border-border/50 hover:bg-muted/20 transition-colors cursor-pointer"
                  >
                    {visibleColumns.map((col) => (
                      <td key={col.name} className="px-4 py-2.5 whitespace-nowrap">
                        <span className="text-foreground">{renderCellValue(row[col.name], col, row)}</span>
                      </td>
                    ))}
                    {hasCountryColumn && (
                      <td className="px-4 py-2.5 max-w-xs">
                        {row['_country_count'] ? (
                          <span className="text-foreground text-xs" title={String(row['_countries'] || '')}>
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-accent/10 text-accent font-medium text-[11px] mr-1">
                              {String(row['_country_count'])}
                            </span>
                            <span className="inline-flex flex-wrap gap-0.5 align-middle">
                              {String(row['_country_codes'] || '').split(',').filter(Boolean).map((code, i) => (
                                <span key={i} title={String(row['_countries'] || '').split(', ')[i] || code} className="text-base leading-none">
                                  {countryFlag(code)}
                                </span>
                              ))}
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic text-xs">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2.5 text-right">
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/50" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4 text-foreground" />
              </button>
              <span className="text-sm text-muted-foreground px-2">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4 text-foreground" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
